import {
    AfterViewChecked,
    Component,
    computed,
    DoCheck,
    ElementRef,
    EventEmitter,
    inject,
    Injector,
    input,
    InputSignal,
    OnInit,
    Output,
    signal,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from "@angular/core";
import { defineCustomElements } from 'vidstack/elements';

import {
    LiquidPageConfiguration,
    LiquidPageContent,
    LiquidPageAnnotation,
    SelectedTextOutput,
    LiquidTooltipMode,
} from "./liquid-page.models";
import {
    LiquidElementComponent
} from "./liquid-element/liquid-element.component";

declare var Mark: any;

@Component({
    selector: 'meta-liquid-page',
    templateUrl: './liquid-page.component.html',
    styleUrls: ['./liquid-page.component.css'],
    encapsulation: ViewEncapsulation.ShadowDom,
})
export class LiquidPageComponent implements DoCheck, OnInit, AfterViewChecked {
    vcr = inject(ViewContainerRef);
    injector = inject(Injector);

    @ViewChild('liquidPage', { static: true }) containerRef: ElementRef | undefined;

    @Output() createdCanvasDataUrl: EventEmitter<string> = new EventEmitter();
    @Output() createAnnotation: EventEmitter<LiquidPageAnnotation> = new EventEmitter();
    @Output() updateAnnotation: EventEmitter<LiquidPageAnnotation> = new EventEmitter();
    @Output() openAnnotation: EventEmitter<LiquidPageAnnotation> = new EventEmitter();
    @Output() deleteAnnotation: EventEmitter<LiquidPageAnnotation> = new EventEmitter();

    $configuration: InputSignal<LiquidPageConfiguration> = input.required({ alias: 'configuration' });
    $content: InputSignal<LiquidPageContent> = input.required({ alias: 'pageContent' });
    $annotations: InputSignal<LiquidPageAnnotation[]> = input.required({ alias: 'pageAnnotions' });
    $annotationsInPage = signal<LiquidPageAnnotation[]>([]);
    $showCut = input<boolean>(false, { alias: 'showCut' });

    $selectedText = signal<string | undefined>(undefined);
    $selectedAnnotation = signal<LiquidPageAnnotation | undefined>(undefined);
    $tooltipCoords = signal<{ top: number; left: number; } | undefined>(undefined);
    $tooltipMode = signal<LiquidTooltipMode>('new');

    $cssClasses = computed(() => {
        let attrValue = "";
        if (this.$configuration()) {
            if (this.$configuration().orientation && this.$configuration().orientation === 'horizontal') {
                attrValue += " page-orientation-horizontal ";
            }

            if (this.$configuration().margins) {
                attrValue += ` page-margins-${this.$configuration().margins} `;
            }

            if (this.$configuration().background) {
                attrValue += ` page-background-${this.$configuration().background} `;
            }

            if (this.$configuration().lineHeight) {
                attrValue += ` page-line-height-${this.$configuration().lineHeight} `;
            }

            if (this.$configuration().fontFamily) {
                attrValue += ` page-font-family-${this.$configuration().fontFamily} `;
            }

            if (this.$configuration().fontSize) {
                attrValue += ` page-font-size-${this.$configuration().fontSize} `;
            }
        }
        return attrValue;
    })

    private markInstance: any;
    private baseMarkOptions = {
        separateWordSearch: false,
        element: 'span',
        accuracy: 'partially',
        caseSensitive: true,
    }

    ngAfterViewChecked(): void {
        if (typeof Mark !== 'undefined' && this.containerRef?.nativeElement && !this.markInstance) {
            this.markInstance = new Mark(this.containerRef?.nativeElement);
        }

        if (this.markInstance) {
            if (this.$annotations().length === 0) {
                this.$annotationsInPage.set([]);
                this.markInstance.unmark();
            } else {
                const annotationsInPageThatWasDeleted = this.$annotationsInPage().filter(item => this.$annotations().map(a => a.id).indexOf(item.id) === -1);
                annotationsInPageThatWasDeleted.forEach(item => this.markInstance.unmark({ className: 'annotation-' + item.id }));
                this.$annotationsInPage.update(cv => cv.filter(item => this.$annotations().map(a => a.id).indexOf(item.id) !== -1));

                this.$annotations().forEach(item => {
                    // Verifico se l'annotazione è già stata gestita
                    let findResult = this.$annotationsInPage().find(itemInPage => itemInPage.id === item.id);
                    if (findResult && JSON.stringify(item) !== JSON.stringify(findResult)) {
                        this.$annotationsInPage.update(cv => cv.filter(item => item.id !== findResult!.id));
                        this.markInstance.unmark({ className: 'annotation-' + findResult.id });
                        findResult = undefined;
                    }

                    if (!findResult) {
                        const self = this;
                        this.markInstance.mark(
                            item.text,
                            {
                                ...this.baseMarkOptions,
                                className: (item.underline ? 'hl-underline' : item.color) + ' annotation-' + item.id,
                                each: function (node: HTMLElement) {
                                    node.addEventListener('click', (evt) => {
                                        self.$selectedAnnotation.set(item);
                                        self.$tooltipMode.set(self.getTooltipModeByType(item.type));
                                        const boundingClientRect = (evt.target as HTMLElement).getBoundingClientRect();
                                        self.$tooltipCoords.set({ top: boundingClientRect.top, left: boundingClientRect.left });
                                    });
                                }
                            }
                        )
                        this.$annotationsInPage.update(cv => { cv.push(item); return cv; });
                    }
                })
            }
        }
    }

    ngOnInit(): void {
        defineCustomElements();
    }

    ngDoCheck(): void {
        if (!this.$content() || !this.containerRef?.nativeElement) {
            return;
        }

        const nativeEl = this.containerRef?.nativeElement as HTMLElement;
        const elementNodes = nativeEl.querySelectorAll('element');
        const elements = this.$content().elements;

        if (elementNodes.length <= 0) {
            return;
        }

        this.$annotationsInPage.set([]);
        elementNodes.forEach(node => {
            const id = node.getAttribute('id');
            const compRef = this.vcr.createComponent(LiquidElementComponent, { injector: this.injector });
            if (id && elements[id]) {
                compRef.instance.$element.set({ ...elements[id], id });
            }
            node.replaceWith(compRef.location.nativeElement);
        });
    }

    handleTextSelection(evt?: SelectedTextOutput) {
        this.$selectedAnnotation.set(undefined);
        this.$tooltipMode.set('new');
        if (!evt) {
            this.$tooltipCoords.set(undefined);
            this.$selectedText.set(undefined);
            this.$selectedAnnotation.set(undefined);
        } else {
            this.$tooltipCoords.set({ top: evt.top, left: evt.left });
            this.$selectedText.set(evt.text);
        }
    }

    clearTextSelection() {
        if (window.getSelection) {
            window.getSelection()!.empty();
            window.getSelection()!.removeAllRanges();
        }
    }

    /* --------------- Tooltip --------------------- */
    getTooltipModeByType(type: string): LiquidTooltipMode {
        switch (type) {
            case 'highlight':
            case 'note':
            case 'link':
                return `edit-${type}`;
            default:
                return 'new';

        }
    }

    handleCreateHighlight(evt: { color: string; }) {
        this.createEmptyAnnotation('highlight', evt.color);
    }

    handleCreateNote() {
        this.createEmptyAnnotation('note');
    }

    handleCreateLink() {
        this.createEmptyAnnotation('link');
    }

    private createEmptyAnnotation(type: string, color?: string) {
        if (this.$selectedText()) {
            const newAnnotation: LiquidPageAnnotation = {
                title: this.$selectedText()!,
                text: type === 'highlight' ? this.$selectedText()! : '',
                color: color ?? '',
                data: {},
                type: type,
            }
            this.createAnnotation.emit(newAnnotation);
            this.handleTextSelection();
            this.clearTextSelection();
        }
    }

    handleUpdateAnnotation(evt: LiquidPageAnnotation) {
        // Rimuovo la nota dalla pagina ed emitto l'evento; la pagina riceverà i dati aggiornati una volta completato il flusso
        this.$annotationsInPage.update(cv => cv.filter(item => item.id !== evt.id));
        if (this.markInstance) {
            this.markInstance.unmark({ className: 'annotation-' + evt.id });
        }

        this.updateAnnotation.emit(evt);
    }
}